import { getConsent, hasConsent, onConsentChange, showConsentBanner } from './consent';

const loadedScripts = new Set();
let activeTrackingConfig = {};
let activeTrackingDisabled = false;
let consentListenerRegistered = false;

const compact = (value) => String(value || '').trim();

const appendScript = ({ id, src, inline }) => {
  if (!id || loadedScripts.has(id) || document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  if (src) script.src = src;
  if (inline) script.text = inline;
  document.head.appendChild(script);
  loadedScripts.add(id);
};

const normalizeTrackingConfig = (config = {}) => ({
  googleAnalytics: compact(config.google_analytics || config.googleAnalytics || config.ga || config.measurementId),
  googleTagManager: compact(config.google_tag_manager || config.googleTagManager || config.gtm || config.tagManagerId),
  microsoftClarity: compact(config.ms_clarity || config.microsoftClarity || config.clarity || config.clarityId),
});

export const loadAllowedTracking = (config = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const tracking = normalizeTrackingConfig(config);

  // GTM is the single integration point when configured. Loading GA or
  // Clarity directly in that case duplicates page views and Clarity sessions.
  if (tracking.googleTagManager && hasConsent('analytics')) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    appendScript({
      id: `privacy-gtm-${tracking.googleTagManager}`,
      src: `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(tracking.googleTagManager)}`,
    });
    return;
  }

  if (tracking.googleAnalytics && hasConsent('analytics')) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', tracking.googleAnalytics, { anonymize_ip: true });
    appendScript({
      id: `privacy-ga-${tracking.googleAnalytics}`,
      src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tracking.googleAnalytics)}`,
    });
  }

  if (tracking.microsoftClarity && hasConsent('analytics')) {
    appendScript({
      id: `privacy-clarity-${tracking.microsoftClarity}`,
      inline: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${tracking.microsoftClarity.replace(/"/g, '')}");`,
    });
  }
};

const applyActiveTracking = () => {
  if (activeTrackingDisabled) return;
  loadAllowedTracking(activeTrackingConfig);
};

export const setupTrackingConsent = (trackingConfig = {}, consentOptions = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const tracking = normalizeTrackingConfig(trackingConfig);
  const optionalCategories = tracking.googleTagManager
    ? ['analytics']
    : [tracking.googleAnalytics || tracking.microsoftClarity ? 'analytics' : null].filter(Boolean);

  if (optionalCategories.length === 0) return;

  activeTrackingConfig = tracking;
  activeTrackingDisabled = consentOptions.disableTracking === true;

  if (getConsent()) {
    applyActiveTracking();
  }

  showConsentBanner({
    ...consentOptions,
    optionalCategories,
    onChange: (consent) => {
      applyActiveTracking();
      if (typeof consentOptions.onChange === 'function') consentOptions.onChange(consent);
    },
  });

  if (!consentListenerRegistered) {
    consentListenerRegistered = true;
    onConsentChange(applyActiveTracking);
  }
};
